import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShieldCheck, Lock, Mail, Eye, EyeOff, ArrowRight,
  AlertCircle, CheckCircle2, Loader2, ArrowLeft,
  Building2, RefreshCw, KeyRound, Search, X
} from 'lucide-react';
import api from '../../api/api';
import './StaffPortalLogin.css';

export default function StaffPortalLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation steps: 'credentials' | 'otp' | 'role_select' | 'change_password'
  const [step, setStep] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Step 1: Credentials
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 2: OTP State
  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const otpInputRefs = useRef([]);

  // Step 3: Multi-Role Selection State
  const [sessionKey, setSessionKey] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(null);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');

  // Step 4: Mandatory Permanent Password Setup (for users with temp passwords)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Focus management
  useEffect(() => {
    if (step === 'otp' && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [step]);

  // URL Search params pre-fill (email & otp)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email') || params.get('identifier');
    const otpParam = params.get('otp') || params.get('code');
    if (emailParam) {
      setIdentifier(emailParam);
    }
    if (otpParam && /^\d{6}$/.test(otpParam.trim())) {
      setOtp(otpParam.trim().split(''));
    }
  }, [location.search]);

  // Resend countdown timer
  useEffect(() => {
    let interval = null;
    if (step === 'otp' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  // Handle Step 1: Credentials Submit
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!identifier.trim() || !password) {
      setErrorMessage('Please enter both your Email / Staff ID and Password.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/staff/login/', {
        identifier: identifier.trim(),
        password: password
      });

      if (res.data?.success && res.data?.otp_sent) {
        setEmail(res.data.email || identifier.trim());
        setMaskedEmail(res.data.masked_email || res.data.email || identifier.trim());
        setStep('otp');
        setResendTimer(60);
        setCanResend(false);
        setSuccessMessage(res.data.message || 'Verification code sent to your email.');
      } else {
        setErrorMessage(res.data?.error || 'Authentication failed. Please check credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setErrorMessage(
        err.response?.data?.error ||
        'Could not authenticate. Access is restricted to invited healthcare & laboratory accounts.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Dedicated OTP Paste Handler (Handles Ctrl+V or right-click paste of 6-digit codes)
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    const cleanDigits = pastedData.replace(/\D/g, '').slice(0, 6);
    if (!cleanDigits) return;

    const newOtp = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = cleanDigits[i] || '';
    }
    setOtp(newOtp);

    // Auto-focus next box or last filled box
    const nextIndex = Math.min(cleanDigits.length, 5);
    if (otpInputRefs.current[nextIndex]) {
      otpInputRefs.current[nextIndex].focus();
    }
  };

  // OTP Input Changes
  const handleOtpChange = (index, value) => {
    const cleanDigits = (value || '').replace(/\D/g, '');
    if (!cleanDigits) {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      return;
    }

    // Handle pasted multi-digit string if onPaste didn't intercept
    if (cleanDigits.length > 1) {
      const digits = cleanDigits.slice(0, 6).split('');
      const newOtp = ['', '', '', '', '', ''];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i] || '';
      }
      setOtp(newOtp);
      const nextIndex = Math.min(digits.length, 5);
      if (otpInputRefs.current[nextIndex]) {
        otpInputRefs.current[nextIndex].focus();
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = cleanDigits.slice(-1);
    setOtp(newOtp);

    // Auto move to next input box
    if (index < 5 && otpInputRefs.current[index + 1]) {
      otpInputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0 && otpInputRefs.current[index - 1]) {
      otpInputRefs.current[index - 1].focus();
    }
  };

  // Handle Step 2: OTP Verification Submit
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setErrorMessage('Please enter the complete 6-digit verification code.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/staff/verify-otp/', {
        email: email,
        otp: otpCode
      });

      if (res.data?.success) {
        // If temporary password active, enforce immediate permanent password configuration
        if (res.data.must_change_password) {
          setCurrentPassword(password);
          setStep('change_password');
          setSuccessMessage('2FA verified. Please configure your new permanent password to complete account setup.');
        } else if (res.data.requires_role_selection) {
          setSessionKey(res.data.session_key);
          setAvailableRoles(res.data.roles || []);
          setStep('role_select');
          setSuccessMessage('Verification confirmed. Select your active workspace.');
        } else {
          // Single role: Complete session immediately
          completeLoginSession(res.data);
        }
      } else {
        setErrorMessage(res.data?.error || 'Invalid verification code.');
      }
    } catch (err) {
      console.error('OTP error:', err);
      setErrorMessage(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    if (!canResend || loading) return;
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await api.post('/api/staff/login/', {
        identifier: identifier.trim() || email,
        password: password
      });
      if (res.data?.success) {
        setResendTimer(60);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);
        setSuccessMessage('A fresh verification code has been dispatched to your email.');
      }
    } catch (err) {
      setErrorMessage('Could not resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Handle Role Selection Submit
  const handleRoleSelect = async (roleObj, index) => {
    setSelectedRoleIndex(index);
    setLoading(true);
    setErrorMessage('');

    try {
      const res = await api.post('/api/staff/select-role/', {
        session_key: sessionKey,
        role: roleObj.role,
        facility_id: roleObj.facility_id
      });

      if (res.data?.success) {
        if (res.data.must_change_password) {
          setCurrentPassword(password);
          setStep('change_password');
          setSuccessMessage('Workspace selected. Please configure your new permanent password to complete setup.');
        } else {
          completeLoginSession(res.data);
        }
      } else {
        setErrorMessage(res.data?.error || 'Failed to initialize session for selected role.');
      }
    } catch (err) {
      console.error('Role select error:', err);
      setErrorMessage(err.response?.data?.error || 'Failed to enter selected portal workspace.');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Handle Permanent Password Change Submit
  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const targetEmail = email || identifier.trim();

    if (!currentPassword) {
      setErrorMessage('Please enter your current temporary password.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setErrorMessage('Please enter and confirm your new permanent password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('New password and confirmation password do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setErrorMessage('Your new password cannot be the same as your temporary password. Please choose a different password.');
      return;
    }
    if (newPassword.length < 8 || newPassword.length > 32) {
      setErrorMessage('Password must be between 8 and 32 characters long.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one uppercase letter (A-Z).');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one lowercase letter (a-z).');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one numeric digit (0-9).');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one special character (!@#$%^&* etc).');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/staff/set-permanent-password/', {
        email: targetEmail,
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      if (res.data?.success) {
        setSuccessMessage('Permanent password configured successfully. Entering portal...');
        setTimeout(() => {
          completeLoginSession(res.data);
        }, 500);
      } else {
        setErrorMessage(res.data?.error || 'Failed to update permanent password.');
      }
    } catch (err) {
      console.error('Password change error:', err);
      setErrorMessage(err.response?.data?.error || 'Could not update password. Please check your temporary password.');
    } finally {
      setLoading(false);
    }
  };

  // Shared Helper: Complete Login & Redirect
  const completeLoginSession = (authData) => {
    const { token, role, user, dashboard } = authData;

    // Save tokens and profile to localStorage per project standards
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('staff_token', token);

      if (role === 'lab_owner' || role === 'lab_manager' || role === 'laboratory_director' || role === 'technical_supervisor') {
        localStorage.setItem('lab_token', token);
      } else if (role === 'doctor' || role === 'provider_office_staff') {
        localStorage.setItem('doctor_token', token);
      } else if (role === 'lab_technician' || role === 'medical_laboratory_scientist' || role === 'accessioning_staff' || role === 'qc_user') {
        localStorage.setItem('technician_token', token);
      } else if (role === 'phlebotomist' || role === 'courier') {
        localStorage.setItem('phleb_token', token);
      } else if (role === 'mobile_phlebotomy_manager') {
        localStorage.setItem('hub_token', token);
      } else if (role === 'super_admin' || role === 'support_admin') {
        localStorage.setItem('super_admin_token', token);
        localStorage.setItem('admin_token', token);
        localStorage.setItem('is_superuser', 'true');
        localStorage.setItem('is_staff', 'true');
      } else if (role === 'hr_admin' || role === 'employer' || role === 'facility_admin' || role === 'nonprofit_admin' || role === 'nonprofit_coordinator') {
        localStorage.setItem('employer_token', token);
      }
    }

    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('portal_user', JSON.stringify(user));
      if (role === 'hr_admin' || role === 'employer' || role === 'facility_admin' || role === 'nonprofit_admin' || role === 'nonprofit_coordinator') {
        localStorage.setItem('employer_user', JSON.stringify(user));
      } else if (role === 'lab_owner' || role === 'lab_manager' || role === 'laboratory_director' || role === 'technical_supervisor') {
        localStorage.setItem('lab_user', JSON.stringify(user));
      } else if (role === 'doctor' || role === 'provider_office_staff') {
        localStorage.setItem('doctor_user', JSON.stringify(user));
      } else if (role === 'lab_technician' || role === 'medical_laboratory_scientist') {
        localStorage.setItem('technician_user', JSON.stringify(user));
      } else if (role === 'phlebotomist' || role === 'courier') {
        localStorage.setItem('phleb_user', JSON.stringify(user));
      } else if (role === 'mobile_phlebotomy_manager') {
        localStorage.setItem('hub_user', JSON.stringify(user));
      } else if (role === 'super_admin' || role === 'support_admin') {
        localStorage.setItem('admin_user', JSON.stringify(user));
      }
    }

    // Direct routing to chosen dashboard
    let targetPath = (
      (role === 'super_admin' || role === 'support_admin') ? '/superadmin/dashboard' :
      dashboard || (
        role === 'facility_admin' || role === 'facility_nurse' ? '/portal/facility/dashboard' :
        role === 'nonprofit_admin' || role === 'nonprofit_coordinator' ? '/portal/nonprofit/dashboard' :
        role === 'hr_admin' || role === 'employer' ? '/portal/employer' :
        role === 'lab_owner' ? '/portal/lab-owner/dashboard' :
        role === 'lab_manager' ? '/portal/lab-manager/dashboard' :
        role === 'doctor' || role === 'provider_office_staff' ? '/portal/doctor/dashboard' :
        role === 'lab_technician' ? '/portal/technician/dashboard' :
        role === 'sales' ? '/portal/sales/dashboard' :
        '/portal/lab-manager/dashboard'
      )
    );
    if (targetPath === '/super-admin/dashboard') {
      targetPath = '/superadmin/dashboard';
    }

    navigate(targetPath, { replace: true, state: location.state });
  };

  return (
    <div className="staff-auth-viewport">
      <div className={`staff-auth-container ${step === 'role_select' ? 'multi-role-wide' : ''}`}>
        
        {/* Header */}
        <div className="staff-auth-header">
          <div className="staff-brand-logo-pill">
            <img 
              src="/images/MusB_Diagnostic_Logo.png" 
              alt="MusB Diagnostics" 
              onError={(e) => { e.currentTarget.src = '/images/MusB_Diagnostic_Logo.webp'; }}
            />
          </div>
          <h1 className="staff-auth-title">Enterprise Staff Portal</h1>
          <p className="staff-auth-subtitle">
            Secure clinical, administrative &amp; diagnostic workspace
          </p>
          <div className="staff-invite-notice-pill">
            <ShieldCheck size={14} /> Access by Invitation Only • Managed by LIS
          </div>
        </div>

        {/* Card */}
        <div className="staff-auth-card">
          
          {/* Alerts */}
          {errorMessage && (
            <div className="staff-auth-alert error">
              <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>{errorMessage}</div>
            </div>
          )}
          {successMessage && (
            <div className="staff-auth-alert success">
              <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>{successMessage}</div>
            </div>
          )}

          {/* ================================================================
              STAGE 1: Credentials (Email / Staff ID + Password)
             ================================================================ */}
          {step === 'credentials' && (
            <form onSubmit={handleCredentialsSubmit}>
              <div className="staff-form-group">
                <label className="staff-form-label" htmlFor="staff-identifier">
                  Official Email or Staff ID
                </label>
                <div className="staff-input-wrapper">
                  <Mail size={18} className="staff-input-icon" />
                  <input
                    id="staff-identifier"
                    type="text"
                    className="staff-input"
                    placeholder="e.g. name@practice.com or MGR-10492"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              <div className="staff-form-group">
                <div className="staff-form-label">
                  <label htmlFor="staff-password">Account Password</label>
                </div>
                <div className="staff-input-wrapper">
                  <Lock size={18} className="staff-input-icon" />
                  <input
                    id="staff-password"
                    type={showPassword ? 'text' : 'password'}
                    className="staff-input"
                    placeholder="Enter account password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="staff-input-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="staff-auth-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Verifying Credentials...
                  </>
                ) : (
                  <>
                    Continue with 2FA Verification <ArrowRight size={18} />
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.8rem', color: '#64748b' }}>
                Self-registration is disabled. If you need account credentials, contact your Laboratory Owner or Hub Administrator.
              </div>
            </form>
          )}

          {/* ================================================================
              STAGE 2: Email OTP Verification (6-digit code)
             ================================================================ */}
          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit}>
              <div className="otp-screen-instruction">
                A 6-digit verification code has been dispatched to:
                <div style={{ marginTop: 6 }}>
                  <span className="otp-screen-email-badge">{maskedEmail}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 6 }}>
                  Check your inbox and spam folder. Code expires in 10 minutes.
                </div>
              </div>

              <div className="otp-inputs-row" onPaste={handleOtpPaste}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (otpInputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onPaste={handleOtpPaste}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    className="otp-box"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              <button
                type="submit"
                className="staff-auth-btn"
                disabled={loading || otp.join('').length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Verifying Code...
                  </>
                ) : (
                  <>
                    Verify & Enter Portal <ArrowRight size={18} />
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    style={{ background: 'transparent', border: 'none', color: '#38bdf8', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    <RefreshCw size={14} style={{ display: 'inline', marginRight: 4 }} /> Resend 6-Digit Code
                  </button>
                ) : (
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Resend code available in {resendTimer}s
                  </span>
                )}
              </div>

              <button
                type="button"
                className="staff-secondary-link"
                onClick={() => {
                  setStep('credentials');
                  setOtp(['', '', '', '', '', '']);
                  setErrorMessage('');
                }}
              >
                <ArrowLeft size={16} /> Sign in with different credentials
              </button>
            </form>
          )}

          {/* ================================================================
              STAGE 3: Multi-Role Selection (Single Gmail holding > 1 role)
             ================================================================ */}
          {step === 'role_select' && (() => {
            const filteredRoles = availableRoles.map((r, originalIdx) => ({ ...r, originalIdx })).filter(r => {
              if (!roleSearchTerm.trim()) return true;
              const term = roleSearchTerm.trim().toLowerCase();
              return (
                (r.facility_name || '').toLowerCase().includes(term) ||
                (r.role_name || '').toLowerCase().includes(term) ||
                (r.facility_id || '').toLowerCase().includes(term) ||
                (r.badge || '').toLowerCase().includes(term) ||
                (r.category || '').toLowerCase().includes(term) ||
                (r.main_access || '').toLowerCase().includes(term)
              );
            });

            return (
              <div>
                <div className="staff-role-picker-header">
                  <h2 className="staff-role-picker-title">Select Active Workspace</h2>
                  <p className="staff-role-picker-desc">
                    Your authenticated account <span style={{ color: '#38bdf8', fontWeight: 600 }}>{email}</span> is authorized for multiple diagnostic laboratories or workspaces. Choose which laboratory to enter:
                  </p>
                </div>

                {/* Search Bar for Assigned Laboratories & Workspaces */}
                <div className="staff-workspace-search-wrap" style={{ position: 'relative', marginTop: '1rem', marginBottom: '0.85rem' }}>
                  <Search size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                  <input
                    type="text"
                    className="staff-input"
                    style={{ paddingLeft: '40px', paddingRight: roleSearchTerm ? '40px' : '14px', height: '44px', background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px' }}
                    placeholder="Search assigned laboratories (name, hub ID, or role)..."
                    value={roleSearchTerm}
                    onChange={(e) => setRoleSearchTerm(e.target.value)}
                    autoFocus
                  />
                  {roleSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setRoleSearchTerm('')}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                      title="Clear search"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', fontSize: '0.82rem', color: '#94a3b8', padding: '0 2px' }}>
                  <span>
                    Showing <strong style={{ color: '#38bdf8' }}>{filteredRoles.length}</strong> of {availableRoles.length} assigned laboratories
                  </span>
                  {roleSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setRoleSearchTerm('')}
                      style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
                    >
                      Clear Filter
                    </button>
                  )}
                </div>

                {filteredRoles.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '2.5rem 1rem',
                    background: 'rgba(30, 41, 59, 0.35)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(255, 255, 255, 0.12)',
                    color: '#94a3b8'
                  }}>
                    <Building2 size={36} style={{ margin: '0 auto 10px', opacity: 0.4, color: '#38bdf8' }} />
                    <p style={{ margin: 0, fontWeight: 600, color: '#e2e8f0', fontSize: '0.95rem' }}>
                      No laboratories match "{roleSearchTerm}"
                    </p>
                    <p style={{ margin: '4px 0 12px', fontSize: '0.8rem' }}>
                      Try searching by laboratory name, hub code, or role title.
                    </p>
                    <button
                      type="button"
                      onClick={() => setRoleSearchTerm('')}
                      style={{
                        background: 'rgba(56, 189, 248, 0.15)',
                        border: '1px solid rgba(56, 189, 248, 0.3)',
                        color: '#38bdf8',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        cursor: 'pointer',
                        fontSize: '0.82rem',
                        fontWeight: 600
                      }}
                    >
                      Reset Filter
                    </button>
                  </div>
                ) : (
                  <div className="staff-roles-grid">
                    {filteredRoles.map((r) => (
                      <div
                        key={r.originalIdx}
                        className={`staff-role-card ${selectedRoleIndex === r.originalIdx ? 'selected' : ''}`}
                        onClick={() => handleRoleSelect(r, r.originalIdx)}
                      >
                        <div>
                          <div className="role-card-top">
                            <span 
                              className="role-badge"
                              style={{
                                background: `${r.color || '#3b82f6'}22`,
                                color: r.color || '#38bdf8',
                                border: `1px solid ${r.color || '#38bdf8'}44`
                              }}
                            >
                              {r.badge || 'STAFF'}
                            </span>
                            <span className="role-category-tag">{r.category || 'Enterprise Role'}</span>
                          </div>

                          <h3 className="role-name-title">{r.role_name}</h3>
                          <div className="role-facility-name">
                            <Building2 size={15} /> {r.facility_name}
                          </div>
                          {r.facility_id && (
                            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px', fontFamily: 'monospace' }}>
                              ID: {r.facility_id}
                            </div>
                          )}
                          <p className="role-access-desc">{r.main_access}</p>
                        </div>

                        <div className="role-select-btn">
                          <span>Enter as {r.role_name.split('/')[0].trim()}</span>
                          {selectedRoleIndex === r.originalIdx && loading ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <ArrowRight size={16} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className="staff-secondary-link"
                  onClick={() => {
                    setStep('credentials');
                    setSessionKey('');
                    setAvailableRoles([]);
                    setRoleSearchTerm('');
                    setErrorMessage('');
                  }}
                >
                  <ArrowLeft size={16} /> Back to credential login
                </button>
              </div>
            );
          })()}

          {/* ================================================================
              STAGE 4: Mandatory Permanent Password Setup (Temp Password Flow)
             ================================================================ */}
          {step === 'change_password' && (
            <form onSubmit={handleChangePasswordSubmit}>
              <div className="staff-pw-stage-header">
                <div className="staff-pw-badge">
                  <KeyRound size={13} /> Action Required
                </div>
                <h2 className="staff-pw-title">Set Permanent Password</h2>
                <p className="staff-pw-desc">
                  You signed in using a temporary access password. Please confirm your temporary password and establish a secure permanent password.
                </p>
              </div>

              {/* Current / Temporary Password */}
              <div className="staff-form-group">
                <label className="staff-form-label" htmlFor="staff-temp-password">
                  Current / Temporary Password
                </label>
                <div className="staff-input-wrapper">
                  <Lock size={18} className="staff-input-icon" />
                  <input
                    id="staff-temp-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="staff-input"
                    placeholder="Enter temporary password from email"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="staff-input-eye-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="staff-form-group">
                <label className="staff-form-label" htmlFor="staff-new-password">
                  New Permanent Password
                </label>
                <div className="staff-input-wrapper">
                  <KeyRound size={18} className="staff-input-icon" />
                  <input
                    id="staff-new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    className="staff-input"
                    placeholder="Create new password (8-16 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="staff-input-eye-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm New Password */}
              <div className="staff-form-group">
                <label className="staff-form-label" htmlFor="staff-confirm-password">
                  Verify New Password
                </label>
                <div className="staff-input-wrapper">
                  <CheckCircle2 size={18} className="staff-input-icon" />
                  <input
                    id="staff-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="staff-input"
                    placeholder="Re-enter new password to verify"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="staff-input-eye-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Real-time Security Rules Checklist */}
              <div className="staff-pw-reqs-card">
                <div className="staff-pw-reqs-title">Security Requirements</div>
                <div className="staff-pw-reqs-grid">
                  <div className={`staff-pw-req-item ${newPassword.length >= 8 && newPassword.length <= 32 ? 'met' : 'unmet'}`}>
                    <div className="staff-pw-req-dot" />
                    <span>8 to 32 characters</span>
                  </div>
                  <div className={`staff-pw-req-item ${/[A-Z]/.test(newPassword) ? 'met' : 'unmet'}`}>
                    <div className="staff-pw-req-dot" />
                    <span>1 uppercase letter (A-Z)</span>
                  </div>
                  <div className={`staff-pw-req-item ${/[a-z]/.test(newPassword) ? 'met' : 'unmet'}`}>
                    <div className="staff-pw-req-dot" />
                    <span>1 lowercase letter (a-z)</span>
                  </div>
                  <div className={`staff-pw-req-item ${/[0-9]/.test(newPassword) ? 'met' : 'unmet'}`}>
                    <div className="staff-pw-req-dot" />
                    <span>1 numeric digit (0-9)</span>
                  </div>
                  <div className={`staff-pw-req-item ${/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'met' : 'unmet'}`}>
                    <div className="staff-pw-req-dot" />
                    <span>1 special character (!@#$)</span>
                  </div>
                  <div className={`staff-pw-req-item ${newPassword && confirmPassword && newPassword === confirmPassword ? 'met' : 'unmet'}`}>
                    <div className="staff-pw-req-dot" />
                    <span>Passwords match</span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="staff-auth-btn"
                disabled={
                  loading ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  !(newPassword.length >= 8 && newPassword.length <= 16) ||
                  !/[A-Z]/.test(newPassword) ||
                  !/[a-z]/.test(newPassword) ||
                  !/[0-9]/.test(newPassword) ||
                  !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ||
                  newPassword !== confirmPassword ||
                  newPassword === currentPassword
                }
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Saving Password...
                  </>
                ) : (
                  <>
                    Set Permanent Password &amp; Enter Portal <ArrowRight size={18} />
                  </>
                )}
              </button>

              <button
                type="button"
                className="staff-secondary-link"
                onClick={() => {
                  setStep('credentials');
                  setOtp(['', '', '', '', '', '']);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setErrorMessage('');
                }}
              >
                <ArrowLeft size={16} /> Sign in with different credentials
              </button>
            </form>
          )}

        </div>

        {/* Footer */}
        <div className="staff-auth-footer">
          CLIA Certified • CAP Accredited Diagnostic Operations • HIPAA Encrypted
        </div>

      </div>
    </div>
  );
}
